import React, { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { api, endpoints } from '../api/config';

const CARD_CONFIG = [
  { key: 'open', label: 'Open', color: 'primary.main' },
  { key: 'notAvailable', label: 'Not Available', color: 'warning.main' },
  { key: 'history', label: 'History', color: 'info.main' },
];
const PARTS_GRID_PAGE_SIZE = 1000;

const PartsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    open: 0,
    notAvailable: 0,
    history: 0,
    openData: [],
    notAvailableData: [],
    historyData: [],
  });
  const [selectedCard, setSelectedCard] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchInput, setGlobalSearchInput] = useState('');
  const [globalSearchTimer, setGlobalSearchTimer] = useState(null);
  const [searchTerms, setSearchTerms] = useState({
    open: '',
    notAvailable: '',
    history: '',
  });

  const refs = {
    open: useRef(null),
    notAvailable: useRef(null),
    history: useRef(null),
  };

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(endpoints.partsDashboard);
      const responseData = response.data?.data || response.data || {};
      
      // Ensure counts are numbers, not objects or arrays
      const getCount = (value) => {
        if (typeof value === 'number') return value;
        if (Array.isArray(value)) return value.length;
        if (typeof value === 'object' && value !== null) return 0;
        const parsed = parseInt(String(value), 10);
        return isNaN(parsed) ? 0 : parsed;
      };
      
      // Ensure data arrays are properly formatted - use direct keys
      const getDataArray = (key) => {
        if (Array.isArray(responseData[key])) {
          return responseData[key];
        }
        return [];
      };
      
      setData({
        open: getCount(responseData.new_count),
        notAvailable: getCount(responseData.not_available_count),
        history: getCount(responseData.history_count),
        openData: getDataArray('new'),
        notAvailableData: getDataArray('Not Available'),
        historyData: getDataArray('History'),
      });
    } catch (err) {
      console.error('Error fetching parts dashboard:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load parts dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCardClick = (key) => {
    setSelectedCard(key);
    setTimeout(() => scrollToSection(refs[key]), 100);
  };

  const handleSearchChange = (key, value) => {
    setSearchTerms(prev => ({ ...prev, [key]: value }));
  };



  const filterData = (items, searchTerm) => {
    const hasLocal = searchTerm && searchTerm.trim() !== '';
    const hasGlobal = globalSearch && globalSearch.trim() !== '';
    if (!hasLocal && !hasGlobal) return items;
    const localTerm = hasLocal ? searchTerm.toLowerCase().trim() : null;
    const globalTerm = hasGlobal ? globalSearch.toLowerCase().trim() : null;
    return items.filter(item => {
      return Object.values(item || {}).some(value => {
        if (value === null || value === undefined) return false;
        const v = String(value).toLowerCase();
        const matchLocal = localTerm ? v.includes(localTerm) : false;
        const matchGlobal = globalTerm ? v.includes(globalTerm) : false;
        return matchLocal || matchGlobal;
      });
    });
  };

  const getTableColumns = (items) => {
    if (!items || items.length === 0) return [];
    
    // User requested order:
    // 1. Po number, 2. So number, 3. Customer name, 4. Item description, 
    // 5. Shipmethod, 6. Tracking number, 7. Missing status
    const primaryOrder = [
      { id: 'po_number', label: 'Po number', alternatives: ['ponumber', 'po number', 'po#'] },
      { id: 'so_number', label: 'So number', alternatives: ['sonumber', 'so number', 'so#', 'transaction_id'] },
      { id: 'customer_name', label: 'Customer name', alternatives: ['customer name', 'customername'] },
      { id: 'item_description', label: 'Item description', alternatives: ['description', 'item description', 'item'] },
      { id: 'shipmethod', label: 'Shipmethod', alternatives: ['ship_method', 'shipvia', 'ship method'] },
      { id: 'tracking_number', label: 'Tracking number', alternatives: ['tracking number', 'trackingnumber'] },
      { id: 'missing_status', label: 'Missing status', alternatives: ['status', 'missing status'] }
    ];

    const firstItem = items[0];
    const allKeys = Object.keys(firstItem || {});
    const orderedCols = [];
    const usedKeys = new Set();

    // Map primary requested columns to actual data keys
    primaryOrder.forEach(target => {
      const actualKey = allKeys.find(k => {
        const lowerK = k.toLowerCase().replace(/_/g, ' ');
        return target.id === k || 
               target.label.toLowerCase() === lowerK || 
               target.alternatives.some(alt => alt.toLowerCase() === lowerK || alt.toLowerCase().replace(/ /g, '_') === k.toLowerCase());
      });

      if (actualKey) {
        orderedCols.push({
          accessorKey: actualKey,
          header: target.label
        });
        usedKeys.add(actualKey);
      }
    });

    // Add any remaining columns
    allKeys.forEach(key => {
      if (!usedKeys.has(key)) {
        orderedCols.push({
          accessorKey: key,
          header: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).replace(/_/g, ' ').trim(),
        });
      }
    });

    return orderedCols;
  };

  const renderCell = (row, key) => {
    try {
      const value = row[key];
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          return value.length > 0 ? value.map(v => String(v)).join(', ') : '-';
        }
        // For objects, try to find a display value or stringify
        if (value.toString && value.toString() !== '[object Object]') {
          return String(value.toString());
        }
        return JSON.stringify(value);
      }
      return String(value);
    } catch (e) {
      return '-';
    }
  };
  const toDataGridColumns = (columns) =>
    (columns || []).map((col) => ({
      field: col.accessorKey,
      headerName: col.header,
      sortable: true,
      resizable: col.resizable !== false,
      flex: col.flex ?? (col.width ? undefined : 1),
      width: col.width,
      minWidth: col.minWidth ?? col.minSize ?? col.size ?? 80,
      renderCell: (params) => renderCell(params.row, col.accessorKey),
    }));

  const summary = {
    open: data.open,
    notAvailable: data.notAvailable,
    history: data.history,
  };

  const sectionGridHeight = selectedCard ? '100%' : (denseMode ? 400 : 480);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: selectedCard ? '100%' : 'auto', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 1 },
            mt: { xs: 1, md: 1 },
            mb: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            flexShrink: 0,
          }}
        >
          {selectedCard && (
            <IconButton
              onClick={() => setSelectedCard(null)}
              sx={{ mr: -1 }}
              aria-label="go back to cards view"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>Parts Dashboard</Typography>
          <TextField
            label="Global Search"
            placeholder="Search across all tables..."
            value={globalSearchInput}
            onChange={(e) => {
              setGlobalSearchInput(e.target.value);
              // Clear existing timer
              if (globalSearchTimer) {
                clearTimeout(globalSearchTimer);
              }
              // Set new timer with fast debounce (100ms)
              const timer = setTimeout(() => {
                setGlobalSearch(e.target.value);
              }, 100);
              setGlobalSearchTimer(timer);
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, maxWidth: 260, mt: { xs: 1, md: 0.5 } }}
          />
          <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, mt: { xs: 1, md: 0.5 } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{ height: 32, px: 1.5, minWidth: 80 }}
            >
              Refresh
            </Button>
            <GlobalDenseModeToolbar
              denseMode={denseMode}
              denseModeTogglePending={denseModeTogglePending}
              persistDenseMode={persistDenseMode}
            />
          </Box>
        </Box>

        {selectedCard === null && (
          <Box sx={{ mb: 3, flexShrink: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              flexWrap: 'wrap'
            }}>
              {CARD_CONFIG.map(({ key, label, color }) => (
                <Box key={key} sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
                  minWidth: 0
                }}>
                  <Card 
                    elevation={2} 
                    onClick={() => handleCardClick(key)}
                    sx={{ 
                      width: '100%',
                      height: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: '4px solid',
                      borderColor: color,
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 }
                    }}
                  >
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      p: 2,
                      '&:last-child': { pb: 2 }
                    }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 0.5, 
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          lineHeight: 1.43
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 600, 
                          color: color,
                          fontSize: '2.125rem',
                          lineHeight: 1.235
                        }}
                      >
                        {typeof summary[key] === 'number' ? summary[key] : (Array.isArray(summary[key]) ? summary[key].length : 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2, color: 'error.main', flexShrink: 0 }}>{error}</Box>
        )}

        <Box
          sx={{
            flex: selectedCard ? 1 : 'none',
            minHeight: selectedCard ? 0 : 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: selectedCard ? 'hidden' : 'auto',
          }}
        >
          {(selectedCard ? [CARD_CONFIG.find(c => c.key === selectedCard)] : CARD_CONFIG).map(({ key, label, color }) => {
            const items = data[`${key}Data`] || [];
            const searchTerm = searchTerms[key] || '';
            const filteredItems = filterData(items, searchTerm);
            const columns = getTableColumns(items);
            const sectionRef = refs[key];
            const isCollapsed = !!collapsedSections[key];

            return (
              <Box
                key={key}
                ref={sectionRef}
                sx={{
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: selectedCard ? 1 : 'none',
                  minHeight: selectedCard ? 0 : 'auto',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2, gap: 2, flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => toggleSection(key)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>({filteredItems.length})</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 300 }}
                  />
                </Stack>

                {isCollapsed ? null : (
                  filteredItems.length === 0 ? (
                    <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                      {items.length === 0 ? 'No data available.' : 'No items match your search.'}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        flex: selectedCard ? 1 : 'none',
                        minHeight: selectedCard ? 0 : sectionGridHeight,
                        height: sectionGridHeight,
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      <HoverExpandDataGridPro
                        disableColumnResize={false}
                        disableVirtualization
                        rows={filteredItems.map((row, index) => ({
                          ...row,
                          id: row.id ?? row._id ?? row.internal_id ?? row.transaction_id ?? `${key}-${index}`,
                        }))}
                        columns={toDataGridColumns(columns)}
                        getRowId={(row) => String(row.id)}
                        disableRowSelectionOnClick
                        hideFooterSelectedRowCount
                        pagination
                        paginationMode="client"
                        pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                        initialState={{ pagination: { paginationModel: { pageSize: PARTS_GRID_PAGE_SIZE, page: 0 } } }}
                        rowHeight={denseMode ? 36 : 52}
                        columnHeaderHeight={denseMode ? 40 : 56}
                        rowBufferPx={120}
                        columnBufferPx={120}
                        sx={{
                          height: '100%',
                          width: '100%',
                          border: 'none',
                        }}
                      />
                    </Box>
                  )
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

    </DashboardLayout>
  );
};

export default PartsDashboard;
